import {put, call, all} from 'redux-saga/effects';
import TimeOff from "../../entities/timeOff";
import * as Actions from "../actions/timeOffActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type TimeOffAction = {
    timeOffId: string;
    timeOff: TimeOff;
    timeOffs: TimeOff[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneTimeOff(action: TimeOffAction) {
	const session = tabSession.captureGeneration();
	try {
		const timeOff: TimeOff = yield call(api.findOne, 'timeOff', action.timeOffId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.TIMEOFF_FETCH_ONE_SUCCESS, timeOffId: action.timeOffId, timeOff, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TIMEOFF_FETCH_ONE_FAILURE, timeOffId: action.timeOffId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {TimeOffAction} action
 */
export function* saveTimeOff(action: TimeOffAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ TIMEOFF_SAVE saga', action.timeOff?.id);

		if (action.timeOff === undefined || action.timeOff === null) {
			console.error('saveTimeOff received a null');
			throw new Error('saveTimeOff received a null');
		}

		if (!(action.timeOff instanceof TimeOff)) {
			action.timeOff = new TimeOff(action.timeOff);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.timeOff.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.timeOff);
			const addResult = yield call(idb.add, 'timeOff', action.timeOff);
			if (addResult.isErr()) {
				console.error(`⮂ saveTimeOff: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertTimeOffSave(action.timeOff, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.TIMEOFF_SAVE_FAILURE, timeOff: action.timeOff, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.TIMEOFF_SAVE, timeOff: action.timeOff, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertTimeOffSave(action.timeOff, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.TIMEOFF_SAVE_FAILURE, timeOff: action.timeOff, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {TimeOffAction} action
 * @param {TimeOff[]} action.timeOffs
 */
export function* saveManyTimeOffs(action: TimeOffAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ TIMEOFF_SAVE_MANY saga', action.timeOffs.map(timeOff => timeOff.id).join(','));

        if (!Array.isArray(action.timeOffs)) {
            console.error('saveManyTimeOffs expected an array, received', action.timeOffs);
            throw new Error('saveManyTimeOffs expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveTimeOff), under these queue keys
		const dispatchedAddedOns: number[] = action.timeOffs.map(timeOff => timeOff?.addedOn);
		const revertAll = () => action.timeOffs.map((timeOff, i) =>
			put(Actions.revertTimeOffSave(timeOff, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: TimeOff[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.timeOffs.length; i++) {
				const timeOff = action.timeOffs[i];
				timeOff.persisted = false;
				ensureOperationId(timeOff);
				toSave.push(timeOff);
			}

			const addResult = yield call(idb.add, 'timeOff', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyTimeOffs: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.TIMEOFF_SAVE_MANY_FAILURE, timeOffs: action.timeOffs, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.TIMEOFF_SAVE_MANY, timeOffs: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.TIMEOFF_SAVE_MANY_FAILURE, timeOffs: action.timeOffs, status: 500, err});

			console.error(`Error saving ${action.timeOffs.length} timeOffs to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {TimeOffAction} action
 */
export function* removeTimeOff(action: TimeOffAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.timeOff === undefined || action.timeOff === null || !(action.timeOff instanceof TimeOff)) {
			console.error('removeTimeOff expected type TimeOff, received', action.timeOff);
			reportQueued(false);
			throw new Error('removeTimeOff expected type TimeOff');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.timeOff.status = 'D';
		action.timeOff.addedOn = newHRTimestamp();
		action.timeOff.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.timeOff);
		const addResult = yield call(idb.add, 'timeOff', action.timeOff);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeTimeOff: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.TIMEOFF_REMOVE_FAILURE, timeOff: action.timeOff, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TIMEOFF_REMOVE, timeOff: action.timeOff});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TIMEOFF_REMOVE_FAILURE, timeOff: action.timeOff, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this TimeOff.
	*
	* @param {TimeOffAction} action
	*/
export function* restoreTimeOff(action: TimeOffAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreTimeOff expected getCurrentUserId() to return a value');
        return;
    }

	if (action.timeOff === undefined || action.timeOff === null) {
        console.error('restoreTimeOff expected a TimeOff, received null');
        throw new Error('restoreTimeOff expected a TimeOff, received null');
    }
    else if (!(action.timeOff instanceof TimeOff)) action.timeOff = new TimeOff(action.timeOff); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.timeOff.status = 'U';
		action.timeOff.addedOn = newHRTimestamp();
		action.timeOff.addedBy = userId;
		action.timeOff.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.timeOff);
		const addResult = yield call(idb.add, 'timeOff', action.timeOff);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreTimeOff: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TIMEOFF_RESTORE_FAILURE, timeOff: action.timeOff, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TIMEOFF_RESTORE, timeOff: action.timeOff});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TIMEOFF_RESTORE_FAILURE, timeOff: action.timeOff, status: 500, err});

		console.error('Error restoring from database', err);
	}
}